import {
    Action,
    ActionWithPayload,
    createAction,
    withMatcher,
} from '../../utils/reducer/reducer.utils';
import { ATTACHMENT_ACTION_TYPES } from './attachment.type';

export type UploadAttachmentStart = ActionWithPayload<
    ATTACHMENT_ACTION_TYPES.UPLOAD_ATTACHMENT_START,
    {
        file: File;
        attachmentType: 'document' | 'model3d';
        nodeId: number;
        onComplete: () => void;
    }
>;

export type UploadAttachmentSuccess =
    Action<ATTACHMENT_ACTION_TYPES.UPLOAD_ATTACHMENT_SUCCESS>;

export type UploadAttachmentFailed = ActionWithPayload<
    ATTACHMENT_ACTION_TYPES.UPLOAD_ATTACHMENT_FAILED,
    string
>;

export type ApproveAttachmentStart = ActionWithPayload<
    ATTACHMENT_ACTION_TYPES.APPROVE_ATTACHMENT_START,
    {
        attachmentId: number;
        attachmentType: 'document' | 'model3d';
        onComplete: () => void;
    }
>;

export type ApproveAttachmentSuccess =
    Action<ATTACHMENT_ACTION_TYPES.APPROVE_ATTACHMENT_SUCCESS>;

export type ApproveAttachmentFailed = ActionWithPayload<
    ATTACHMENT_ACTION_TYPES.APPROVE_ATTACHMENT_FAILED,
    string
>;

export type RejectAttachmentStart = ActionWithPayload<
    ATTACHMENT_ACTION_TYPES.REJECT_ATTACHMENT_START,
    {
        attachmentId: number;
        attachmentType: 'document' | 'model3d';
        refusalNotes: string;
        onComplete: () => void;
    }
>;

export type RejectAttachmentSuccess =
    Action<ATTACHMENT_ACTION_TYPES.REJECT_ATTACHMENT_SUCCESS>;

export type RejectAttachmentFailed = ActionWithPayload<
    ATTACHMENT_ACTION_TYPES.REJECT_ATTACHMENT_FAILED,
    string
>;

export type ReplaceAttachmentStart = ActionWithPayload<
    ATTACHMENT_ACTION_TYPES.REPLACE_ATTACHMENT_START,
    {
        attachmentId: number;
        attachmentType: 'document' | 'model3d';
        file: File;
        onComplete: () => void;
    }
>;

export type ReplaceAttachmentSuccess =
    Action<ATTACHMENT_ACTION_TYPES.REPLACE_ATTACHMENT_SUCCESS>;

export type ReplaceAttachmentFailed = ActionWithPayload<
    ATTACHMENT_ACTION_TYPES.REPLACE_ATTACHMENT_FAILED,
    string
>;

export type CreateScene3dStart = ActionWithPayload<
    ATTACHMENT_ACTION_TYPES.CREATE_SCENE_3D_START,
    {
        model_3d: number;
        nodeId: number;
        onComplete: () => void;
    }
>;

export type CreateScene3dSuccess =
    Action<ATTACHMENT_ACTION_TYPES.CREATE_SCENE_3D_SUCCESS>;

export type CreateScene3dFailed = ActionWithPayload<
    ATTACHMENT_ACTION_TYPES.CREATE_SCENE_3D_FAILED,
    string
>;

export type ShareScene3dStart = ActionWithPayload<
    ATTACHMENT_ACTION_TYPES.SHARE_SCENE_3D_START,
    {
        sceneId: number;
        validUntil: string;
        onComplete: ({ svg, url, code }: { svg: string; url: string; code: string; }) => void;
    }
>;

export type ShareScene3dSuccess =
    Action<ATTACHMENT_ACTION_TYPES.SHARE_SCENE_3D_SUCCESS>;

export type ShareScene3dFailed = ActionWithPayload<
    ATTACHMENT_ACTION_TYPES.SHARE_SCENE_3D_FAILED,
    string
>;

export type SendShareCodeMailStart = ActionWithPayload<
    ATTACHMENT_ACTION_TYPES.SEND_SHARE_CODE_MAIL_START,
    {
        shareCode: string;
        email: string;
        onComplete: () => void;
    }
>;

export type SendShareCodeMailSuccess =
    Action<ATTACHMENT_ACTION_TYPES.SEND_SHARE_CODE_MAIL_SUCCESS>;

export type SendShareCodeMailFailed = ActionWithPayload<
    ATTACHMENT_ACTION_TYPES.SEND_SHARE_CODE_MAIL_FAILED,
    string
>;

export const uploadAttachmentStart = withMatcher(
    (
        file: File,
        attachmentType: 'document' | 'model3d',
        nodeId: number,
        onComplete: () => void
    ): UploadAttachmentStart =>
        createAction(ATTACHMENT_ACTION_TYPES.UPLOAD_ATTACHMENT_START, {
            file,
            attachmentType,
            nodeId,
            onComplete,
        })
);

export const uploadAttachmentSuccess = withMatcher(
    (): UploadAttachmentSuccess =>
        createAction(ATTACHMENT_ACTION_TYPES.UPLOAD_ATTACHMENT_SUCCESS)
);

export const uploadAttachmentFailed = withMatcher(
    (error: string): UploadAttachmentFailed =>
        createAction(ATTACHMENT_ACTION_TYPES.UPLOAD_ATTACHMENT_FAILED, error)
);

export const approveAttachmentStart = withMatcher(
    (
        attachmentId: number,
        attachmentType: 'document' | 'model3d',
        onComplete: () => void
    ): ApproveAttachmentStart =>
        createAction(ATTACHMENT_ACTION_TYPES.APPROVE_ATTACHMENT_START, {
            attachmentId,
            attachmentType,
            onComplete,
        })
);

export const approveAttachmentSuccess = withMatcher(
    (): ApproveAttachmentSuccess =>
        createAction(ATTACHMENT_ACTION_TYPES.APPROVE_ATTACHMENT_SUCCESS)
);

export const approveAttachmentFailed = withMatcher(
    (error: string): ApproveAttachmentFailed =>
        createAction(ATTACHMENT_ACTION_TYPES.APPROVE_ATTACHMENT_FAILED, error)
);

export const rejectAttachmentStart = withMatcher(
    (
        attachmentId: number,
        attachmentType: 'document' | 'model3d',
        refusalNotes: string,
        onComplete: () => void
    ): RejectAttachmentStart =>
        createAction(ATTACHMENT_ACTION_TYPES.REJECT_ATTACHMENT_START, {
            attachmentId,
            attachmentType,
            refusalNotes,
            onComplete,
        })
);

export const rejectAttachmentSuccess = withMatcher(
    (): RejectAttachmentSuccess =>
        createAction(ATTACHMENT_ACTION_TYPES.REJECT_ATTACHMENT_SUCCESS)
);

export const rejectAttachmentFailed = withMatcher(
    (error: string): RejectAttachmentFailed =>
        createAction(ATTACHMENT_ACTION_TYPES.REJECT_ATTACHMENT_FAILED, error)
);

export const replaceAttachmentStart = withMatcher(
    (
        attachmentId: number,
        attachmentType: 'document' | 'model3d',
        file: File,
        onComplete: () => void
    ): ReplaceAttachmentStart =>
        createAction(ATTACHMENT_ACTION_TYPES.REPLACE_ATTACHMENT_START, {
            attachmentId,
            attachmentType,
            file,
            onComplete,
        })
);

export const replaceAttachmentSuccess = withMatcher(
    (): ReplaceAttachmentSuccess =>
        createAction(ATTACHMENT_ACTION_TYPES.REPLACE_ATTACHMENT_SUCCESS)
);

export const replaceAttachmentFailed = withMatcher(
    (error: string): ReplaceAttachmentFailed =>
        createAction(ATTACHMENT_ACTION_TYPES.REPLACE_ATTACHMENT_FAILED, error)
);

export const createScene3dStart = withMatcher(
    (
        model_3d: number,
        nodeId: number,
        onComplete: () => void
    ): CreateScene3dStart =>
        createAction(ATTACHMENT_ACTION_TYPES.CREATE_SCENE_3D_START, {
            model_3d,
            nodeId,
            onComplete,
        })
);

export const createScene3dSuccessSuccess = withMatcher(
    (): CreateScene3dSuccess =>
        createAction(ATTACHMENT_ACTION_TYPES.CREATE_SCENE_3D_SUCCESS)
);

export const createScene3dFailed = withMatcher(
    (error: string): CreateScene3dFailed =>
        createAction(ATTACHMENT_ACTION_TYPES.CREATE_SCENE_3D_FAILED, error)
);

export const shareScene3dStart = withMatcher(
    (
        sceneId: number,
        validUntil: string,
        onComplete: ({ svg, url, code }: { svg: string; url: string; code: string; }) => void
    ): ShareScene3dStart =>
        createAction(ATTACHMENT_ACTION_TYPES.SHARE_SCENE_3D_START, {
            sceneId,
            validUntil,
            onComplete,
        })
);

export const shareScene3dSuccess = withMatcher(
    (): ShareScene3dSuccess =>
        createAction(ATTACHMENT_ACTION_TYPES.SHARE_SCENE_3D_SUCCESS)
);

export const shareScene3dFailed = withMatcher(
    (error: string): ShareScene3dFailed =>
        createAction(ATTACHMENT_ACTION_TYPES.SHARE_SCENE_3D_FAILED, error)
);

export const sendShareCodeMailStart = withMatcher(
    (
        shareCode: string,
        email: string,
        onComplete: () => void
    ): SendShareCodeMailStart =>
        createAction(ATTACHMENT_ACTION_TYPES.SEND_SHARE_CODE_MAIL_START, {
            shareCode,
            email,
            onComplete,
        })
);

export const sendShareCodeMailSuccess = withMatcher(
    (): SendShareCodeMailSuccess =>
        createAction(ATTACHMENT_ACTION_TYPES.SEND_SHARE_CODE_MAIL_SUCCESS)
);

export const sendShareCodeMailFailed = withMatcher(
    (error: string): SendShareCodeMailFailed =>
        createAction(ATTACHMENT_ACTION_TYPES.SEND_SHARE_CODE_MAIL_FAILED, error)
);
